import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { teamAPI, notesAPI, foldersAPI, workspaceAPI } from "../../lib/api";

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

const Badge = ({ label, color = ACCENT }) => (
  <span style={{ fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono', monospace", color, background: `${color}18`, border: `1px solid ${color}33`, borderRadius: 4, padding: "2px 7px" }}>{label}</span>
);

const Avatar = ({ name, color = ACCENT, size = 32 }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%", flexShrink: 0,
    background: `${color}22`, border: `2px solid ${color}44`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: size * 0.35, fontWeight: 700, color, fontFamily: "'DM Sans', sans-serif",
  }}>
    {name.split(" ").map(n => n[0]).join("").slice(0, 2)}
  </div>
);

// ─── shared colours per user ───
// Previously mapped 4 demo teammates (Alex Johnson / Sarah Kim / Marcus Lee
// / Jessica Park) to specific accent colours. Those users no longer exist
// in the (empty) team activity dataset. Now the function hashes the name
// into one of a small palette so real teammates always get a stable colour
// without any hardcoded identity leakage.
const _COLOR_PALETTE = [ACCENT, "#22C55E", "#8B5CF6", "#F59E0B", "#EF4444", "#06B6D4"];
const userColor = name => {
  if (!name || name === "System") return TEXT_DIM;
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0x7fffffff;
  return _COLOR_PALETTE[hash % _COLOR_PALETTE.length];
};

// ─────────────────────────────────────
// TEAM ACTIVITY DATA
// ─────────────────────────────────────
// Previously seeded with demo records (Sarah Kim / Marcus Lee / Alex Johnson
// / Jessica Park collaborating on Shopify Inc., Klarna AB, etc.) so the
// Team Activity / Decision Notes / Shared Folders screens looked populated
// during early-access demos. They displayed as REAL data in production for
// solo founders who had never invited a teammate, which was confusing and
// misleading. All three datasets are now empty by default; screens render
// their own empty-state messaging when no real records exist.
const ACTIVITY = [];

const actionIcon = type => ({
  analysis: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
  note: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
  article: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
  monitor: "M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z",
  export: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4",
  batch: "M4 6h16M4 10h16M4 14h16M4 18h16",
  folder: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z",
}[type] || "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z");

const actionColor = type => ({
  analysis: ACCENT, note: "#8B5CF6", article: "#22C55E",
  monitor: "#F59E0B", export: "#06B6D4", batch: "#EC4899", folder: "#F97316",
}[type] || TEXT_DIM);

// ─────────────────────────────────────
// DECISION NOTES DATA — demo records removed (see top-of-file comment)
// ─────────────────────────────────────
const NOTES = [];

const priorityColor = p => ({ high: "#EF4444", medium: "#F59E0B", low: "#22C55E" }[p] || TEXT_DIM);

// ─────────────────────────────────────
// SHARED FOLDERS DATA — demo records removed (see top-of-file comment)
// ─────────────────────────────────────
const FOLDERS_DATA = [];

const permissionColor = p => ({ Org: "#8B5CF6", Team: ACCENT, Private: TEXT_DIM }[p] || TEXT_DIM);

// ═══════════════════════════════════════════
// TEAM ACTIVITY SCREEN
// ═══════════════════════════════════════════
const TeamActivityScreen = ({ teamId, workspaceId }) => {
  const [filterUser, setFilterUser] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivity = async () => {
      // Prefer workspace-scoped activity (works for solo users too). Falls
      // back to team activity if a teamId is explicitly passed and no
      // workspaceId is available.
      try {
        if (workspaceId) {
          const res = await workspaceAPI.activity(workspaceId, { limit: 200 });
          setActivities(res.data || []);
        } else if (teamId) {
          const res = await teamAPI.activity(teamId);
          setActivities(res.data || []);
        } else {
          setActivities([]);
        }
      } catch (err) { console.error('Failed to load activity', err); toast.error("Could not load team data."); }
      finally { setLoading(false); }
    };
    fetchActivity();
  }, [teamId, workspaceId]);

  const users = ["All", ...Array.from(new Set(activities.map(a => a.user_name)))];
  const types = ["All", "analysis", "note", "monitor", "article", "export", "batch", "folder", "team"];

  const filtered = activities.filter(a => {
    if (filterUser !== "All" && a.user_name !== filterUser) return false;
    if (filterType !== "All" && a.type !== filterType) return false;
    return true;
  });

  // group by date
  const grouped = filtered.reduce((acc, a) => {
    const date = a.created_at ? new Date(a.created_at).toLocaleDateString() : "Today";
    if (!acc[date]) acc[date] = [];
    acc[date].push(a);
    return acc;
  }, {});

  return (
    <div>
      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 24 }}>
        {[
          { label: "Actions Today", value: activities.filter(a => new Date(a.created_at).toDateString() === new Date().toDateString()).length, color: TEXT_PRIMARY },
          { label: "Analyses Run", value: activities.filter(a => a.type === "analysis").length, color: ACCENT },
          { label: "Notes Added", value: activities.filter(a => a.type === "note").length, color: "#8B5CF6" },
          { label: "Team Events", value: activities.filter(a => a.type === "team").length, color: "#06B6D4" },
        ].map((s, i) => (
          <div key={i} style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px 18px" }}>
            <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.05em", marginBottom: 6 }}>{s.label.toUpperCase()}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.02em" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {users.map(u => (
            <button key={u} onClick={() => setFilterUser(u)} style={{
              fontSize: 11, padding: "5px 11px", borderRadius: 6, cursor: "pointer",
              border: `1px solid ${filterUser === u ? (u === "All" ? ACCENT : userColor(u) + "88") : BORDER}`,
              background: filterUser === u ? (u === "All" ? `${ACCENT}18` : `${userColor(u)}14`) : "transparent",
              color: filterUser === u ? (u === "All" ? ACCENT : userColor(u)) : TEXT_MUTED,
              fontFamily: "'DM Sans', sans-serif",
            }}>{u}</button>
          ))}
        </div>
        <div style={{ width: 1, height: 18, background: BORDER }} />
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {types.map(t => (
            <button key={t} onClick={() => setFilterType(t)} style={{
              fontSize: 11, padding: "5px 11px", borderRadius: 6, cursor: "pointer",
              border: `1px solid ${filterType === t ? actionColor(t) + "88" : BORDER}`,
              background: filterType === t ? `${actionColor(t)}14` : "transparent",
              color: filterType === t ? actionColor(t) : TEXT_MUTED,
              fontFamily: "'DM Sans', sans-serif", textTransform: t === "All" ? "none" : "capitalize",
            }}>{t}</button>
          ))}
        </div>
      </div>

      {/* Activity feed */}
      {loading ? (
        <div data-testid="activity-loading" style={{ padding: "40px 0", textAlign: "center", color: TEXT_MUTED, fontSize: 13 }}>
          Loading workspace activity…
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div data-testid="activity-empty" style={{ padding: "60px 0", textAlign: "center" }}>
          <p style={{ fontSize: 14, color: TEXT_PRIMARY, marginBottom: 6 }}>
            {activities.length === 0 ? "No activity yet." : "No activity matches your filters."}
          </p>
          <p style={{ fontSize: 12, color: TEXT_MUTED }}>
            {activities.length === 0
              ? "Runs, notes, exports and monitors will show up here as your team uses Inference."
              : "Try clearing the user or type filter."}
          </p>
        </div>
      ) : Object.entries(grouped).map(([date, items]) => (
        <div key={date} style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
            {date.toUpperCase()}
            <div style={{ flex: 1, height: 1, background: BORDER }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {items.map(item => {
              const uc = userColor(item.user_name);
              const ac = actionColor(item.type);
              return (
                <div key={item.activity_id} style={{
                  background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 9,
                  padding: "12px 16px", display: "flex", alignItems: "center", gap: 12,
                  transition: "border-color 0.15s",
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#2D3748"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = BORDER}
                >
                  <Avatar name={item.user_name} color={uc} size={32} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: uc }}>{item.user_name}</span>
                      <span style={{ fontSize: 13, color: TEXT_MUTED }}>{item.action}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY }}>{item.target}</span>
                    </div>
                    <div style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginTop: 3 }}>{item.meta}</div>
                  </div>
                  <div style={{
                    width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                    background: `${ac}18`, border: `1px solid ${ac}33`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={ac} strokeWidth={1.8}>
                      <path d={actionIcon(item.type)} />
                    </svg>
                  </div>
                  <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>{item.created_at ? new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════
// DECISION NOTES SCREEN
// ═══════════════════════════════════════════
const DecisionNotesScreen = () => {
  const [filterCompany, setFilterCompany] = useState("All");
  const [filterPriority, setFilterPriority] = useState("All");
  const [expandedNote, setExpandedNote] = useState(null);
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState({ title: "", company: "", content: "", priority: "medium", tags: [], linked_contacts: [] });
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotes = useCallback(async () => {
    try {
      const res = await notesAPI.list();
      setNotes(res.data || []);
    } catch (err) { console.error('Failed to load notes', err); toast.error("Could not load team data."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleCreateNote = async () => {
    if (!draft.title || !draft.content) return;
    try {
      await notesAPI.create(draft);
      setDraft({ title: "", company: "", content: "", priority: "medium", tags: [], linked_contacts: [] });
      setComposing(false);
      fetchNotes();
    } catch (err) { console.error('Failed to create note', err); }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await notesAPI.delete(noteId);
      fetchNotes();
    } catch (err) { console.error('Failed to delete note', err); }
  };

  const handleTogglePin = async (note) => {
    try {
      await notesAPI.update(note.note_id, { pinned: !note.pinned });
      fetchNotes();
    } catch (err) { console.error('Failed to toggle pin', err); }
  };

  const companies = ["All", ...Array.from(new Set(notes.map(n => n.company).filter(Boolean)))];

  const filtered = notes.filter(n => {
    if (filterCompany !== "All" && n.company !== filterCompany) return false;
    if (filterPriority !== "All" && n.priority !== filterPriority) return false;
    return true;
  });

  const pinned = filtered.filter(n => n.pinned);
  const unpinned = filtered.filter(n => !n.pinned);

  const NoteCard = ({ note }) => {
    const isOpen = expandedNote === note.note_id;
    const pc = priorityColor(note.priority);
    const uc = userColor(note.author_name);
    return (
      <div style={{
        background: SURFACE, border: `1px solid ${note.pinned ? pc + "44" : BORDER}`,
        borderRadius: 10, overflow: "hidden", marginBottom: 8,
        transition: "border-color 0.15s",
      }}>
        <div
          style={{ padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 12 }}
          onClick={() => setExpandedNote(isOpen ? null : note.note_id)}
        >
          {note.pinned && (
            <svg width={14} height={14} viewBox="0 0 24 24" fill={pc} stroke={pc} strokeWidth={1} style={{ marginTop: 3, flexShrink: 0 }}>
              <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>{note.title}</h3>
              <Badge label={note.priority.toUpperCase()} color={pc} />
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Badge label={note.company} color={ACCENT} />
              {(note.tags || []).slice(0, 2).map(t => (
                <span key={t} style={{ fontSize: 10, color: TEXT_DIM, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 3, padding: "1px 6px", fontFamily: "'DM Mono', monospace" }}>{t}</span>
              ))}
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end", marginBottom: 3 }}>
              <Avatar name={note.author_name || "?"} color={uc} size={20} />
              <span style={{ fontSize: 11, color: uc, fontWeight: 500 }}>{(note.author_name || "").split(" ")[0]}</span>
            </div>
            <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{note.created_at ? new Date(note.created_at).toLocaleDateString() : ""}</div>
          </div>
          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2}
            style={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.2s", flexShrink: 0, marginTop: 4 }}>
            <path d="M9 18l6-6-6-6" />
          </svg>
        </div>
        {isOpen && (
          <div style={{ borderTop: `1px solid ${BORDER}`, padding: "16px 18px" }}>
            <p style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.75, marginBottom: 16 }}>{note.content}</p>
            {(note.linked_contacts || []).length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 6, letterSpacing: "0.06em" }}>LINKED CONTACTS</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {note.linked_contacts.map(c => (
                    <span key={c} style={{ fontSize: 11, color: "#22C55E", background: "#052E16", border: "1px solid #166534", borderRadius: 5, padding: "2px 10px", cursor: "pointer" }}>{c}</span>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              <button data-testid={`pin-note-${note.note_id}`} onClick={(e) => { e.stopPropagation(); handleTogglePin(note); }} style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7, padding: "7px 14px", fontSize: 11, color: TEXT_MUTED, cursor: "pointer" }}>{note.pinned ? "Unpin" : "Pin"}</button>
              <button data-testid={`delete-note-${note.note_id}`} onClick={(e) => { e.stopPropagation(); handleDeleteNote(note.note_id); }} style={{ background: "transparent", border: `1px solid #7F1D1D`, borderRadius: 7, padding: "7px 14px", fontSize: 11, color: "#EF4444", cursor: "pointer", marginLeft: "auto" }}>Delete</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Compose */}
      {composing ? (
        <div style={{ background: SURFACE, border: `1px solid ${ACCENT}44`, borderRadius: 10, padding: "18px 20px", marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: ACCENT, fontFamily: "'DM Mono', monospace", marginBottom: 12, letterSpacing: "0.06em" }}>NEW DECISION NOTE</div>
          <input value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} placeholder="Note title…"
            style={{ width: "100%", background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7, padding: "10px 14px", fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, outline: "none", marginBottom: 10, fontFamily: "'DM Sans', sans-serif" }}
            onFocus={e => e.target.style.borderColor = ACCENT} onBlur={e => e.target.style.borderColor = BORDER}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <input type="text" value={draft.company} onChange={e => setDraft(d => ({ ...d, company: e.target.value }))} placeholder="Company name"
              style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7, padding: "9px 12px", fontSize: 13, color: TEXT_PRIMARY, outline: "none", fontFamily: "'DM Sans', sans-serif" }}
            />
            <select style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7, padding: "9px 12px", fontSize: 13, color: TEXT_MUTED, outline: "none", fontFamily: "'DM Sans', sans-serif" }}>
              <option>Medium priority</option><option>High priority</option><option>Low priority</option>
            </select>
          </div>
          <textarea value={draft.content} onChange={e => setDraft(d => ({ ...d, content: e.target.value }))} placeholder="Write your decision note…" rows={4}
            style={{ width: "100%", background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7, padding: "10px 14px", fontSize: 13, color: TEXT_PRIMARY, outline: "none", resize: "vertical", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7 }}
            onFocus={e => e.target.style.borderColor = ACCENT} onBlur={e => e.target.style.borderColor = BORDER}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={() => setComposing(false)} style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "9px 18px", fontSize: 13, color: TEXT_MUTED, cursor: "pointer" }}>Cancel</button>
            <button data-testid="save-note-btn" onClick={handleCreateNote} style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: 8, padding: "9px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Save Note</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setComposing(true)} style={{
          width: "100%", background: `${ACCENT}0A`, border: `1px dashed ${ACCENT}55`,
          borderRadius: 10, padding: "12px", fontSize: 13, color: ACCENT,
          cursor: "pointer", fontFamily: "'DM Sans', sans-serif", marginBottom: 20,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
        }}>
          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M12 4v16m8-8H4" /></svg>
          New Decision Note
        </button>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 5 }}>
          {companies.map(c => (
            <button key={c} onClick={() => setFilterCompany(c)} style={{
              fontSize: 11, padding: "5px 11px", borderRadius: 6, cursor: "pointer",
              border: `1px solid ${filterCompany === c ? ACCENT : BORDER}`,
              background: filterCompany === c ? `${ACCENT}18` : "transparent",
              color: filterCompany === c ? ACCENT : TEXT_MUTED,
              fontFamily: "'DM Sans', sans-serif",
            }}>{c}</button>
          ))}
        </div>
        <div style={{ width: 1, height: 18, background: BORDER }} />
        <div style={{ display: "flex", gap: 5 }}>
          {["All", "high", "medium", "low"].map(p => (
            <button key={p} onClick={() => setFilterPriority(p)} style={{
              fontSize: 11, padding: "5px 11px", borderRadius: 6, cursor: "pointer",
              border: `1px solid ${filterPriority === p ? (p === "All" ? ACCENT : priorityColor(p) + "88") : BORDER}`,
              background: filterPriority === p ? (p === "All" ? `${ACCENT}18` : `${priorityColor(p)}14`) : "transparent",
              color: filterPriority === p ? (p === "All" ? ACCENT : priorityColor(p)) : TEXT_MUTED,
              fontFamily: "'DM Sans', sans-serif", textTransform: "capitalize",
            }}>{p === "All" ? "All" : `${p.charAt(0).toUpperCase() + p.slice(1)}`}</button>
          ))}
        </div>
      </div>

      {/* Pinned */}
      {pinned.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em", marginBottom: 10 }}>PINNED</div>
          {pinned.map(n => <NoteCard key={n.note_id || n.id} note={n} />)}
        </div>
      )}

      {/* All other */}
      <div>
        {pinned.length > 0 && <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em", marginBottom: 10 }}>ALL NOTES</div>}
        {unpinned.map(n => <NoteCard key={n.note_id || n.id} note={n} />)}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// SHARED FOLDERS SCREEN
// ═══════════════════════════════════════════
const SharedFoldersScreen = () => {
  const [expanded, setExpanded] = useState(null);
  const [filterPerm, setFilterPerm] = useState("All");
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newFolder, setNewFolder] = useState({ name: "", description: "", permission: "Team" });

  const fetchFolders = useCallback(async () => {
    try {
      const res = await foldersAPI.list();
      setFolders(res.data || []);
    } catch (err) { console.error('Failed to load folders', err); toast.error("Could not load team data."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  const handleCreateFolder = async () => {
    if (!newFolder.name) return;
    try {
      await foldersAPI.create(newFolder);
      setNewFolder({ name: "", description: "", permission: "Team" });
      setCreating(false);
      fetchFolders();
    } catch (err) { console.error('Failed to create folder', err); }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await foldersAPI.delete(folderId);
      fetchFolders();
    } catch (err) { console.error('Failed to delete folder', err); }
  };

  const filtered = folders.filter(f => filterPerm === "All" || f.permission === filterPerm);
  const FOLDER_COLORS = ["#2563EB", "#22C55E", "#8B5CF6", "#F59E0B", "#EF4444", "#06B6D4"];

  return (
    <div>
      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 24 }}>
        {[
          { label: "Total Folders", value: folders.length },
          { label: "Shared (Org/Team)", value: folders.filter(f => f.permission !== "Private").length, color: ACCENT },
          { label: "Private", value: folders.filter(f => f.permission === "Private").length, color: TEXT_MUTED },
        ].map((s, i) => (
          <div key={i} style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px 18px" }}>
            <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.05em", marginBottom: 6 }}>{s.label.toUpperCase()}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color || TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.02em" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filter + new folder */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center" }}>
        <div style={{ display: "flex", gap: 5 }}>
          {["All", "Org", "Team", "Private"].map(p => (
            <button key={p} onClick={() => setFilterPerm(p)} style={{
              fontSize: 11, padding: "5px 12px", borderRadius: 6, cursor: "pointer",
              border: `1px solid ${filterPerm === p ? permissionColor(p) + "88" : BORDER}`,
              background: filterPerm === p ? `${permissionColor(p)}14` : "transparent",
              color: filterPerm === p ? permissionColor(p) : TEXT_MUTED,
              fontFamily: "'DM Sans', sans-serif",
            }}>{p}</button>
          ))}
        </div>
        <button data-testid="new-folder-btn" onClick={() => setCreating(!creating)} style={{
          marginLeft: "auto", background: ACCENT, color: "#fff", border: "none", borderRadius: 7,
          padding: "8px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer",
          fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", gap: 6,
        }}>
          <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M12 4v16m8-8H4" /></svg>
          New Folder
        </button>
      </div>

      {creating && (
        <div style={{ background: SURFACE, border: `1px solid ${ACCENT}44`, borderRadius: 10, padding: "18px 20px", marginBottom: 16 }}>
          <input value={newFolder.name} onChange={e => setNewFolder(f => ({ ...f, name: e.target.value }))} placeholder="Folder name…"
            data-testid="new-folder-name"
            style={{ width: "100%", background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7, padding: "10px 14px", fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, outline: "none", marginBottom: 10, fontFamily: "'DM Sans', sans-serif" }}
          />
          <input value={newFolder.description} onChange={e => setNewFolder(f => ({ ...f, description: e.target.value }))} placeholder="Description…"
            style={{ width: "100%", background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7, padding: "10px 14px", fontSize: 13, color: TEXT_PRIMARY, outline: "none", marginBottom: 10, fontFamily: "'DM Sans', sans-serif" }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setCreating(false)} style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "9px 18px", fontSize: 13, color: TEXT_MUTED, cursor: "pointer" }}>Cancel</button>
            <button data-testid="create-folder-submit" onClick={handleCreateFolder} style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: 8, padding: "9px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Create Folder</button>
          </div>
        </div>
      )}

      {/* Folder cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map(folder => {
          const isOpen = expanded === folder.folder_id;
          const folderColor = folder.color || FOLDER_COLORS[folders.indexOf(folder) % FOLDER_COLORS.length];
          return (
            <div key={folder.folder_id} style={{
              background: SURFACE, border: `1px solid ${isOpen ? folderColor + "44" : BORDER}`,
              borderRadius: 10, overflow: "hidden", transition: "border-color 0.15s",
            }}>
              <div
                style={{ padding: "16px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}
                onClick={() => setExpanded(isOpen ? null : folder.folder_id)}
              >
                {/* Folder icon */}
                <div style={{
                  width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                  background: `${folderColor}18`, border: `1px solid ${folderColor}33`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke={folderColor} strokeWidth={1.8}>
                    <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>{folder.name}</span>
                    <Badge label={(folder.permission || "Team").toUpperCase()} color={permissionColor(folder.permission)} />
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{folder.companies || 0} companies</span>
                    <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>· {folder.analyses || 0} analyses</span>
                    <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>· {folder.notes || 0} notes</span>
                  </div>
                </div>

                {/* Members */}
                <div style={{ display: "flex", marginRight: 8 }}>
                  {(folder.members || []).slice(0, 3).map((m, i) => (
                    <div key={m.user_id || i} style={{ marginLeft: i > 0 ? -8 : 0, zIndex: (folder.members || []).length - i }}>
                      <Avatar name={m.name || "?"} color={userColor(m.name)} size={26} />
                    </div>
                  ))}
                  {(folder.members || []).length > 3 && (
                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: SURFACE_2, border: `2px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "center", marginLeft: -8, fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>
                      +{folder.members.length - 3}
                    </div>
                  )}
                </div>

                <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>{folder.updated_at ? new Date(folder.updated_at).toLocaleDateString() : ""}</span>
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2}
                  style={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}>
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>

              {isOpen && (
                <div style={{ borderTop: `1px solid ${BORDER}`, padding: "16px 20px" }}>
                  <p style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.65, marginBottom: 16 }}>{folder.description}</p>

                  {/* Members list */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em", marginBottom: 8 }}>MEMBERS</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {(folder.members || []).map((m, i) => (
                        <div key={m.user_id || i} style={{ display: "flex", alignItems: "center", gap: 7, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "5px 10px" }}>
                          <Avatar name={m.name || "?"} color={userColor(m.name)} size={18} />
                          <span style={{ fontSize: 12, color: TEXT_PRIMARY }}>{m.name}</span>
                          {m.role === "owner" && <Badge label="OWNER" color={TEXT_DIM} />}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    <button style={{ background: `${folderColor}14`, border: `1px solid ${folderColor}33`, borderRadius: 7, padding: "8px 16px", fontSize: 12, color: folderColor, cursor: "pointer", fontWeight: 600 }}>Open Folder →</button>
                    <button data-testid={`delete-folder-${folder.folder_id}`} onClick={() => handleDeleteFolder(folder.folder_id)} style={{ background: "transparent", border: `1px solid #7F1D1D`, borderRadius: 7, padding: "8px 14px", fontSize: 12, color: "#EF4444", cursor: "pointer", marginLeft: "auto" }}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════
export default function InferenceTeamScreens({ teamId, workspaceId, initialScreen = "activity" }) {
  const [activeScreen, setActiveScreen] = useState(initialScreen);
  
  // Update activeScreen when initialScreen changes (navigation from sidebar)
  useEffect(() => {
    setActiveScreen(initialScreen);
  }, [initialScreen]);

  const SCREENS = [
    { id: "activity", label: "Team Activity", tier: "Org" },
    { id: "notes", label: "Decision Notes", tier: "Team" },
    { id: "folders", label: "Shared Folders", tier: "Team" },
  ];

  const titles = { activity: "Team Activity", notes: "Decision Notes", folders: "Shared Folders" };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #1E2433; border-radius: 4px; }
        input::placeholder, textarea::placeholder { color: #374151; }
        button { outline: none; }
        select option { background: #0D1117; color: #F9FAFB; }
      `}</style>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 28px" }}>
        {activeScreen === "activity" && <TeamActivityScreen teamId={teamId} workspaceId={workspaceId} />}
        {activeScreen === "notes" && <DecisionNotesScreen />}
        {activeScreen === "folders" && <SharedFoldersScreen />}
      </div>
    </div>
  );
}
